import { Button, Col, Form, InputNumber, Row, Select, Space, Typography } from 'antd';
import {
  CO2SolubilityTableParameters,
  EnthalpyTableParameters,
  KValuesTableParameters,
  IxUnitSystem,
  IxTableRequest,
  IxTableType
} from '../../@types/ghePage5/types';

type Props = {
  projectUid: string;
  tableType: IxTableType;
  tableFilename: string;
  onTableTypeChange: (value: IxTableType) => void;
  onGenerate: (payload: IxTableRequest) => Promise<void>;
  generating?: boolean;
};

const TABLE_OPTIONS: { label: string; value: IxTableType }[] = [
  { label: 'Enthalpy', value: 'enthalpy' },
  { label: 'K-values (Crookston)', value: 'k_values' },
  { label: 'CO2 Solubility', value: 'co2_solubility' }
];

const ENTHALPY_COMPONENT_OPTIONS = [
  { label: 'H2O (Mw 18.01528)', value: 'H2O' },
  { label: 'CO2 (Mw 44.01)', value: 'CO2' },
  { label: 'N2 (Mw 14.015)', value: 'N2' },
  { label: 'H2 (Mw 2.016)', value: 'H2' },
  { label: 'H2S (Mw 34.082)', value: 'H2S' },
  { label: 'CH4 (Mw 16.04)', value: 'CH4' },
  { label: 'C2H6 (Mw 30.07)', value: 'C2H6' },
  { label: 'C3H8 (Mw 44.097)', value: 'C3H8' },
  { label: 'C4H10 (Mw 58.12)', value: 'C4H10' },
  { label: 'C5H12 (Mw 72.15)', value: 'C5H12' },
  { label: 'C6H14 (Mw 86.178)', value: 'C6H14' },
  { label: 'C7H16 (Mw 100.2019)', value: 'C7H16' },
  { label: 'C8H18 (Mw 114.23)', value: 'C8H18' },
  { label: 'C10H22 (Mw 142.29)', value: 'C10H22' },
  { label: 'C12H26 (Mw 170.34)', value: 'C12H26' },
  { label: 'C15H32 (Mw -)', value: 'C15H32' },
  { label: 'C20H42 (Mw -)', value: 'C20H42' }
];

type FormValues = {
  table_filename?: string;
  components?: string[];
  k_values_components?: string[];
  unit_system?: IxUnitSystem;
  salinity_wt?: number;
  temperature_min?: number;
  temperature_max?: number;
  temperature_delta?: number;
  pressure_min?: number;
  pressure_max?: number;
  pressure_delta?: number;
};

export const GheIxTableForm = ({
  projectUid,
  tableType,
  tableFilename,
  onTableTypeChange,
  onGenerate,
  generating
}: Props) => {
  const [form] = Form.useForm<FormValues>();

  const submit = async (values: FormValues) => {
    if (tableType === 'enthalpy') {
      const components = values.components || [];

      const enthalpyParams: EnthalpyTableParameters = {
        components,
        temperature_min: Number(values.temperature_min),
        temperature_max: Number(values.temperature_max),
        temperature_delta: Number(values.temperature_delta),
        pressure_min: Number(values.pressure_min),
        pressure_max: Number(values.pressure_max),
        pressure_delta: Number(values.pressure_delta)
      };

      await onGenerate({
        project_uid: projectUid,
        table_type: tableType,
        table_filename: tableFilename?.trim() || 'tables_export',
        enthalpy_params: enthalpyParams
      });
      return;
    }

    if (tableType === 'k_values') {
      const components = values.k_values_components || [];
      const kValuesParams: KValuesTableParameters = {
        components,
        unit_system: values.unit_system || 'metric'
      };

      await onGenerate({
        project_uid: projectUid,
        table_type: tableType,
        table_filename: tableFilename?.trim() || 'tables_export',
        k_values_params: kValuesParams
      });
      return;
    }

    if (tableType === 'co2_solubility') {
      const co2SolubilityParams: CO2SolubilityTableParameters = {
        salinity_wt: Number(values.salinity_wt) / 100,
        temperature_min: Number(values.temperature_min),
        temperature_max: Number(values.temperature_max),
        temperature_delta: Number(values.temperature_delta),
        pressure_max: Number(values.pressure_max)
      };

      await onGenerate({
        project_uid: projectUid,
        table_type: tableType,
        table_filename: tableFilename?.trim() || 'tables_export',
        co2_solubility_params: co2SolubilityParams
      });
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} data-testid="ghe-page5-form">
      <Typography.Text strong>IX Table Type</Typography.Text>
      <Select
        value={tableType}
        options={TABLE_OPTIONS}
        onChange={onTableTypeChange}
        data-testid="ghe-page5-table-type"
      />
      {tableType === 'enthalpy' ? (
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={submit}
          initialValues={{
            components: ['CO2'],
            unit_system: 'metric',
            salinity_wt: 0.1,
            temperature_min: 20,
            temperature_max: 30,
            temperature_delta: 5,
            pressure_min: 10,
            pressure_max: 20,
            pressure_delta: 5,
            table_filename: 'tables_export'
          }}
          data-testid="ghe-page5-enthalpy-form"
        >
          <Form.Item
            label="Select components for enthalpy tables"
            name="components"
            rules={[{ required: true, message: 'Components are required' }]}
          >
            <Select
              mode="multiple"
              options={ENTHALPY_COMPONENT_OPTIONS}
              placeholder="Select components from dropdown"
              data-testid="ghe-page5-components"
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Minimum Temperature (ºC)"
                name="temperature_min"
                rules={[{ required: true, message: 'Minimum Temperature (ºC) is required' }]}
              >
                <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-temperature-start" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Maximum Temperature (ºC)"
                name="temperature_max"
                rules={[{ required: true, message: 'Maximum Temperature (ºC) is required' }]}
              >
                <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-temperature-end" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Delta Temperature (ºC)"
                name="temperature_delta"
                rules={[
                  { required: true, message: 'Delta Temperature (ºC) is required' },
                  { type: 'number', min: 0.000001, message: 'Delta Temperature (ºC) must be positive' }
                ]}
              >
                <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-temperature-step" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Minimum Pressure (Bar)"
                name="pressure_min"
                rules={[{ required: true, message: 'Minimum Pressure (Bar) is required' }]}
              >
                <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-pressure-start" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Maximum Pressure (Bar)"
                name="pressure_max"
                rules={[{ required: true, message: 'Maximum Pressure (Bar) is required' }]}
              >
                <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-pressure-end" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Delta Pressure (Bar)"
                name="pressure_delta"
                rules={[
                  { required: true, message: 'Delta Pressure (Bar) is required' },
                  { type: 'number', min: 0.000001, message: 'Delta Pressure (Bar) must be positive' }
                ]}
              >
                <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-step" />
              </Form.Item>
            </Col>
          </Row>
          <Button
            htmlType="submit"
            type="primary"
            loading={generating}
            data-testid="ghe-page5-generate"
          >
            Generate
          </Button>
        </Form>
      ) : tableType === 'k_values' ? (
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={submit}
          initialValues={{
            k_values_components: ['CO2'],
            unit_system: 'metric',
            table_filename: 'tables_export'
          }}
          data-testid="ghe-page5-k-values-form"
        >
          <Form.Item
            label="Select components for K-values"
            name="k_values_components"
            rules={[{ required: true, message: 'Components are required' }]}
          >
            <Select
              mode="multiple"
              options={ENTHALPY_COMPONENT_OPTIONS}
              placeholder="Select components from dropdown"
              data-testid="ghe-page5-k-values-components"
            />
          </Form.Item>
          <Form.Item
            label="Unit system"
            name="unit_system"
            rules={[{ required: true, message: 'Unit system is required' }]}
          >
            <Select
              options={[
                { value: 'oilfield', label: 'Oilfield' },
                { value: 'metric', label: 'Metric' }
              ]}
              data-testid="ghe-page5-k-values-unit-system"
            />
          </Form.Item>
          <Button
            htmlType="submit"
            type="primary"
            loading={generating}
            data-testid="ghe-page5-generate"
          >
            Generate
          </Button>
        </Form>
      ) : tableType === 'co2_solubility' ? (
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={submit}
          initialValues={{
            salinity_wt: 10,
            temperature_min: 20,
            temperature_max: 40,
            temperature_delta: 10,
            pressure_max: 10,
            table_filename: 'tables_export'
          }}
          data-testid="ghe-page5-co2-form"
        >
          <Form.Item
            label="Salinity % wt"
            name="salinity_wt"
            rules={[{ required: true, message: 'Salinity % wt is required' }]}
          >
            <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-salinity" />
          </Form.Item>
          <Form.Item
            label="Minimum Temperature (ºC)"
            name="temperature_min"
            rules={[{ required: true, message: 'Minimum Temperature (ºC) is required' }]}
          >
            <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-co2-temperature-start" />
          </Form.Item>
          <Form.Item
            label="Maximum Temperature (ºC)"
            name="temperature_max"
            rules={[{ required: true, message: 'Maximum Temperature (ºC) is required' }]}
          >
            <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-co2-temperature-end" />
          </Form.Item>
          <Form.Item
            label="Delta Temperature (ºC)"
            name="temperature_delta"
            rules={[
              { required: true, message: 'Delta Temperature (ºC) is required' },
              { type: 'number', min: 0.000001, message: 'Delta Temperature (ºC) must be positive' }
            ]}
          >
            <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-temperature-step" />
          </Form.Item>
          <Form.Item
            label="Maximum Pressure (Bar)"
            name="pressure_max"
            rules={[
              { required: true, message: 'Maximum Pressure (Bar) is required' },
              { type: 'number', min: 0.000001, message: 'Maximum Pressure (Bar) must be positive' }
            ]}
          >
            <InputNumber style={{ width: '100%' }} data-testid="ghe-page5-pressure-cap" />
          </Form.Item>
          <Button
            htmlType="submit"
            type="primary"
            loading={generating}
            data-testid="ghe-page5-generate"
          >
            Generate
          </Button>
        </Form>
      ) : (
        <Typography.Text type="secondary">
          Parameters for this table type will be enabled in the next phase.
        </Typography.Text>
      )}
    </Space>
  );
};
